import { FC, useState } from 'react';
import { Box, VStack, HStack, Text, Button, Image } from '@chakra-ui/react';
import {
  DialogRoot,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogActionTrigger,
} from '@/components/ui/dialog';
import { documentService } from '@/services/firebase/documentService';
import { DogDocument } from '@/types/dog';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';
import { FiDownload, FiEye, FiTrash2 } from 'react-icons/fi';

interface DocumentViewerProps {
  dogId: string;
  userId: string;
  document: DogDocument | null;
  onDocumentChange?: () => void;
}

/**
 * 血統書表示コンポーネント
 */
export const DocumentViewer: FC<DocumentViewerProps> = ({
  dogId,
  userId,
  document,
  onDocumentChange,
}) => {
  const [showPreview, setShowPreview] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  /**
   * ダウンロード
   */
  const handleDownload = () => {
    if (!document) return;

    // 新しいタブで開く（ブラウザのダウンロード機能を使用）
    window.open(document.fileUrl, '_blank');
  };

  /**
   * 削除
   */
  const handleDelete = async () => {
    if (!document) return;

    setIsDeleting(true);
    setDeleteError(null);

    try {
      await documentService.deleteDocument(dogId, userId);
      setShowDeleteDialog(false);

      if (onDocumentChange) {
        onDocumentChange();
      }
    } catch (error) {
      console.error('血統書削除エラー:', error);
      setDeleteError(error instanceof Error ? error.message : '削除に失敗しました');
    } finally {
      setIsDeleting(false);
    }
  };

  if (!document) {
    return (
      <Box p={8} textAlign="center" bg="gray.50" borderRadius="md">
        <Text color="gray.500">血統書が登録されていません</Text>
      </Box>
    );
  }

  return (
    <VStack gap={4} align="stretch">
      {/* 血統書情報カード */}
      <Box p={4} bg="white" borderRadius="md" borderWidth={1} borderColor="gray.200" shadow="sm">
        <VStack align="stretch" gap={3}>
          <HStack justify="space-between">
            <VStack align="start" gap={1}>
              <Text fontSize="sm" fontWeight="medium" color="gray.700">
                {document.fileName}
              </Text>
              <Text fontSize="xs" color="gray.500">
                アップロード日時: {format(document.uploadedAt.toDate(), 'yyyy年M月d日 HH:mm', { locale: ja })}
              </Text>
              <Text fontSize="xs" color="gray.500">
                形式: {document.fileType === 'pdf' ? 'PDF' : '画像'}
              </Text>
            </VStack>
          </HStack>

          {/* アクションボタン */}
          <HStack gap={2}>
            <Button
              size="sm"
              colorPalette="blue"
              onClick={() => setShowPreview(true)}
              flex={1}
            >
              <FiEye size={14} />
              <Text ml={1}>プレビュー</Text>
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={handleDownload}
              flex={1}
            >
              <FiDownload size={14} />
              <Text ml={1}>ダウンロード</Text>
            </Button>
            <Button
              size="sm"
              colorPalette="red"
              variant="outline"
              onClick={() => setShowDeleteDialog(true)}
            >
              <FiTrash2 size={14} />
            </Button>
          </HStack>
        </VStack>
      </Box>

      {/* プレビューモーダル */}
      <DialogRoot
        open={showPreview}
        onOpenChange={() => setShowPreview(false)}
        size="xl"
      >
        <DialogContent maxW="90vw" maxH="90vh">
          <DialogHeader>
            <DialogTitle>{document.fileName}</DialogTitle>
          </DialogHeader>
          <DialogBody>
            {document.fileType === 'pdf' ? (
              <Box w="100%" h="70vh">
                <iframe
                  src={document.fileUrl}
                  width="100%"
                  height="100%"
                  style={{ border: 'none' }}
                  title="PDF Preview"
                />
              </Box>
            ) : (
              <Image
                src={document.fileUrl}
                alt={document.fileName}
                w="100%"
                maxH="70vh"
                objectFit="contain"
              />
            )}
          </DialogBody>
          <DialogFooter>
            <HStack gap={2}>
              <Button variant="outline" onClick={handleDownload}>
                <FiDownload size={16} />
                <Text ml={2}>ダウンロード</Text>
              </Button>
              <DialogActionTrigger asChild>
                <Button colorPalette="blue">閉じる</Button>
              </DialogActionTrigger>
            </HStack>
          </DialogFooter>
        </DialogContent>
      </DialogRoot>

      {/* 削除確認ダイアログ */}
      <DialogRoot
        open={showDeleteDialog}
        onOpenChange={() => setShowDeleteDialog(false)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>血統書の削除</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <VStack align="stretch" gap={3}>
              <Text>この血統書を削除してもよろしいですか？</Text>
              <Box p={3} bg="gray.50" borderRadius="md">
                <Text fontSize="sm" color="gray.600">
                  {document.fileName}
                </Text>
              </Box>
              <Text fontSize="sm" color="red.600">
                ⚠️ この操作は元に戻せません。
              </Text>
              {deleteError && (
                <Box p={3} bg="red.50" borderRadius="md" borderWidth={1} borderColor="red.200">
                  <Text fontSize="sm" color="red.700">
                    {deleteError}
                  </Text>
                </Box>
              )}
            </VStack>
          </DialogBody>
          <DialogFooter>
            <HStack gap={2}>
              <DialogActionTrigger asChild>
                <Button variant="outline" disabled={isDeleting}>
                  キャンセル
                </Button>
              </DialogActionTrigger>
              <Button
                colorPalette="red"
                onClick={handleDelete}
                loading={isDeleting}
                disabled={isDeleting}
              >
                {isDeleting ? '削除中...' : '削除'}
              </Button>
            </HStack>
          </DialogFooter>
        </DialogContent>
      </DialogRoot>
    </VStack>
  );
};
